// packages/crm-app/src/components/AddLeadForm.tsx
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import CustomRadioSelect from "rc-app/components/CustomRadioSelect";
import CountryCodeSelect from "rc-app/components/CountryCodeSelect";
import {
  createReCustomer,
  getCustomerList,
  updateCustomer,
} from "rc-app/api/customerActionsApi";
import { toast } from "react-toastify";
// import { getAgentProfile } from "rc-app/api/agentActionsApi";
// import UserService from "rc-app/services/UserService";

import {
  isValidPhoneNumber,
  getExampleNumber,
  getCountries,
  getCountryCallingCode,
  CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import UserService from "rc-app/services/UserService";
import { getAgentProfile, getAllAgent } from "rc-app/api/agentActionsApi";
import {
  getClientAdminByEmailId,
  getClientManagerByEmailId,
} from "rc-app/api/systemBusinessAdminActionApi";
import { getAgencyById } from "rc-app/api/agencyActionsApi";
import CustomCheckboxSelect from "rc-app/components/CustomCheckboxSelect";

interface Lead {
  id: string;
  status: "hot" | "warm" | "cold" | "customer" | "denied" | "";
  fullName: string;
  mobile: string;
  email: string;
  interestedIn?: string;
  city?: string;
  linkedIn?: string;
  addressLine1?: string;
  addressLine2?: string;
  landmark?: string;
  pincode?: string;
  state?: string;
  referredBy?: string;
  investmentType?: string;
  annualIncome?: string;
  budget?: string;
  occupationType?: string;
  incomeSource?: string;
  familySize?: string;
}

// interface AddLeadFormProps {
//   onCancel: () => void;
//   lead?: Lead | null;
// }

const basicDetailsSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  countryCode: Yup.string().required("Country code is required"),
  mobile: Yup.string()
    .required("Mobile number is required")
    .test(
      "valid-phone",
      "Invalid mobile number for selected country",
      function (value) {
        const { countryCode } = this.parent;
        if (!value || !countryCode) return false;
        return isValidPhoneNumber(`${countryCode}${value}`);
      },
    ),
  email: Yup.string().email("Invalid email").required("Email is required"),
});

// const additionalDetailsSchema = Yup.object({
//   interestedIn: Yup.array().min(1, "Select at least one interest"),
//   investmentType: Yup.string().required("Investment type is required"),
//   budgetInr: Yup.string().required("Budget is required"),
//   occupationType: Yup.string().required("Occupation type is required"),
//   incomeSource: Yup.string().required("Income source is required"),
//   familySize: Yup.string().required("Family size is required"),
// });

export const AddLeadForm: React.FC = () => {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const location = useLocation();
  const lead = (location.state as { lead?: Lead })?.lead || null;
  const isEditMode = !!leadId;

  const [activeTab, setActiveTab] = useState<"basic" | "additional">("basic");
  // const [showStatusOptions, setShowStatusOptions] = useState(false);
  // const [showInterestedOptions, setShowInterestedOptions] = useState(false);
  // const [showInvestmentOptions, setShowInvestmentOptions] = useState(false);
  // const [showBudgetOptions, setShowBudgetOptions] = useState(false);
  // const [showOccupationOptions, setShowOccupationOptions] = useState(false);
  // const [showIncomeSourceOptions, setShowIncomeSourceOptions] = useState(false);

  const [firstName, lastName] = lead?.fullName.split(" ") || ["", ""];

  const [agentId, setAgentId] = useState();
  const [agencyId, setAgencyId] = useState();

  // Lead-origin (Task 9): who created this platform lead. Set once at creation;
  // backend derives source="platform". actor_role ∈ aba|asa|agent.
  const [actorRole, setActorRole] = useState<"aba" | "asa" | "agent" | "">("");
  const [actorUserId, setActorUserId] = useState<string>("");

  // ASA creating a lead must route it to one of their own agents.
  const isABA =
    UserService.hasRole("agency_business_admin") ||
    UserService.hasRole("super_admin");
  const isASA = UserService.hasRole("agency_system_admin") && !isABA;
  const [asaAgents, setAsaAgents] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [assignedAgentId, setAssignedAgentId] = useState<string>("");
  const [agentError, setAgentError] = useState<string>("");

  const [mobileError, setMobileError] = useState("");
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const mobileCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (UserService.hasRole("agent")) {
      getAgentProfile(UserService.getUseremail()).then((res) => {
        setAgentId(res?.agent?._id);
        setActorRole("agent");
        setActorUserId(res?.agent?._id || "");
        getAgencyById(res?.agent?.agency["id"]).then((res) => {
          setAgencyId(res?._id);
        });
      });
    }
    if (UserService.hasRole("agency_business_admin")) {
      getClientManagerByEmailId(UserService.getUseremail()).then((res) => {
        setAgencyId(res?.agency_business_admin?.agency_id);
        setActorRole("aba");
        setActorUserId(res?.agency_business_admin?._id || "");
      });
    }
    if (UserService.hasRole("agency_system_admin")) {
      getClientAdminByEmailId(UserService.getUseremail()).then((res) => {
        const aid = res?.agency_system_admin?.agency_id;
        const sid = res?.agency_system_admin?._id || "";
        setAgencyId(aid);
        setActorRole("asa");
        setActorUserId(sid);
        if (aid) {
          getAllAgent(0, 10000, aid)
            .then((r: any) => {
              const team = (r?.re_agent_list || [])
                .filter((ag: any) => {
                  const raw = ag.assigned_asa_id || ag.asa_id;
                  const id = raw
                    ? String(Array.isArray(raw) ? raw[0] : raw)
                    : "";
                  return id && sid && id === sid;
                })
                .map((ag: any) => ({
                  id: ag._id,
                  name:
                    `${ag.first_name || ""} ${ag.last_name || ""}`.trim() ||
                    ag.email_id ||
                    "Agent",
                }));
              setAsaAgents(team);
            })
            .catch(() => {});
        }
      });
    }
  }, []);

  const checkMobile = async (mobile: string) => {
    if (
      !mobile.trim() ||
      !isValidPhoneNumber(`${formik.values.countryCode}${mobile}`)
    ) {
      return;
    }
    if (isEditMode || !agencyId) return;

    setCheckingMobile(true);
    try {
      const res = await getCustomerList(0, 1, agencyId, "", mobile);
      if (res?.re_customer_list?.length > 0) {
        setMobileError(
          "Lead with this mobile number already exists in your agency",
        );
      } else {
        setMobileError("");
      }
    } catch (error) {
      console.error("Error checking mobile:", error);
    } finally {
      setCheckingMobile(false);
    }
  };

  const checkEmail = async (email: string) => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }
    if (isEditMode || !agencyId) return;

    setCheckingEmail(true);
    try {
      const res = await getCustomerList(0, 1, agencyId, "", "", email);
      if (res?.re_customer_list?.length > 0) {
        setEmailError("Lead with this email already exists in your agency");
      } else {
        setEmailError("");
      }
    } catch (error) {
      console.error("Error checking email:", error);
    } finally {
      setCheckingEmail(false);
    }
  };

  console.log("setAgentID", agentId);

  const formik = useFormik({
    initialValues: {
      leadStatus: lead?.status
        ? lead.status.charAt(0).toUpperCase() + lead.status.slice(1)
        : "Hot",
      firstName: firstName,
      lastName: lastName,
      countryCode: "+91",
      mobile: lead?.mobile.replace(/\+91\s/, "") || "",
      email: lead?.email || "",
      addressLine1: lead?.addressLine1 || "",
      addressLine2: lead?.addressLine2 || "",
      landmark: lead?.landmark || "",
      city: lead?.city || "",
      state: lead?.state || "",
      pincode: lead?.pincode || "",
      linkedIn: lead?.linkedIn || "",
      referredBy: lead?.referredBy || "",
      interestedIn: lead?.interestedIn
        ? lead.interestedIn.split(", ")
        : ([] as string[]),
      investmentType: lead?.investmentType || "",
      budgetInr: lead?.budget || "",
      occupationType: lead?.occupationType || "",
      incomeSource: lead?.incomeSource || "",
      familySize: lead?.familySize || "",
      annualIncome: lead?.annualIncome || "",
    },
    validationSchema: basicDetailsSchema,

    onSubmit: async (values, { setSubmitting }) => {
      if (isASA && !isEditMode && !assignedAgentId) {
        toast.error("Please select an agent to assign this lead to");
        setSubmitting(false);
        return;
      }
      try {
        const payload = {
          agency_id: agencyId,
          email_id: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          country_code: values.countryCode,
          mobile_number: values.mobile,
          gender: "",
          date_of_birth: "",
          profile_pic: "",
          address: {
            address_line1: values.addressLine1,
            address_line2: values.addressLine2,
            landmark: values.landmark,
            postal_code: values.pincode,
            city: values.city,
            province: values.state,
            country: "",
          },
          property_shortlist: [] as string[],
          agent_id: UserService.hasRole("agent")
            ? [agentId]
            : isASA && !isEditMode && assignedAgentId
              ? [assignedAgentId]
              : null,
          joined_date: new Date().toISOString().split("T")[0],
          linkedin_profile: values.linkedIn,
          referred_by_mobile_number: values.referredBy,
          interested_in: values.interestedIn,
          investment_type: values.investmentType,
          annual_income: values.annualIncome,
          budget: values.budgetInr,
          occupation_type: values.occupationType,
          income_source: values.incomeSource,
          family_size: values.familySize ? Number(values.familySize) : 0,
        };

        // await createReCustomer(payload);
        if (isEditMode) {
          await updateCustomer(leadId, payload);
        } else {
          // Stamp lead origin once at creation: source=platform (derived by
          // backend) + who created it. Omitted if role/id didn't resolve.
          await createReCustomer({
            ...payload,
            ...(actorRole && actorUserId
              ? { actor_role: actorRole, actor_user_id: actorUserId }
              : {}),
          });
        }

        toast.success(
          isEditMode ? "Lead updated successfully" : "Lead added successfully",
        );
        navigate("/leads");
      } catch (error) {
        if (mobileError || emailError) {
          toast.error("Please fix the errors before submitting");
          setSubmitting(false);
          return;
        }
        toast.error("Failed to save lead. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    console.log("Form values:", formik.values);
  }, [formik.values]);

  const handleNext = () => {
    if (activeTab === "basic") {
      if (mobileError || emailError) {
        toast.error("Please fix the errors before proceeding");
        return;
      }
      if (isASA && !isEditMode && !assignedAgentId) {
        setAgentError("Please select an agent to assign this lead to");
        toast.error("Please select an agent to assign this lead to");
        return;
      }
      formik.validateForm().then((errors) => {
        if (Object.keys(errors).length === 0) {
          setActiveTab("additional");
        } else {
          formik.setTouched({
            firstName: true,
            lastName: true,
            mobile: true,
            email: true,
            addressLine1: true,
            city: true,
            state: true,
            pincode: true,
          });
        }
      });
    } else {
      formik.handleSubmit();
    }
  };

  const getMaxDigits = (): number => {
    try {
      const allCountries = getCountries();
      const country = allCountries.find(
        (c: CountryCode) =>
          `+${getCountryCallingCode(c)}` === formik.values.countryCode,
      );
      if (!country) return 15;
      const example = getExampleNumber(country, examples);
      return example?.nationalNumber?.length || 15;
    } catch {
      return 15;
    }
  };
  const getPlaceholder = (): string => {
    try {
      const allCountries = getCountries();
      const country = allCountries.find(
        (c: CountryCode) =>
          `+${getCountryCallingCode(c)}` === formik.values.countryCode,
      );
      if (!country) return "Enter...";
      const example = getExampleNumber(country, examples);
      return example?.nationalNumber || "Enter...";
    } catch {
      return "Enter...";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] text-white p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm mb-4 px-1">
        <button
          onClick={() => navigate("/leads")}
          className="text-[#889DB2] hover:text-white transition-colors"
        >
          My Leads
        </button>
        <span className="text-[#889DB2]">/</span>
        <span className="text-white font-medium">
          {isEditMode ? "Edit Lead" : "Add New Lead"}
        </span>
      </div>

      <div className="flex gap-2 mb-6 justify-center">
        <div className="flex items-center bg-[#2E4051] px-4 py-2 mt-6 rounded-lg gap-2">
          <button
            className={`px-3 py-2 rounded-md ${
              activeTab === "basic" && "bg-[#008564]"
            }`}
            // onClick={() => setActiveTab("basic")}
          >
            Basic Details
          </button>
          <button
            className={`px-3 py-2 rounded-md ${
              activeTab === "additional" && "bg-[#008564]"
            }`}
            // onClick={() => setActiveTab("additional")}
          >
            Additional Details
          </button>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        {activeTab === "basic" && (
          <div className="max-w-4xl mx-auto">
            {/* <fieldset className="border border-white rounded-lg p-6 mb-6">
              <legend className="px-4 text-lg text-center w-auto mx-auto">
                Status
              </legend>
              <label className="block mb-2">Lead Status</label>
              <div
                className={`flex items-center justify-between w-full px-3 py-2 border rounded-md text-[#F4F5F7] bg-[#2E4051] focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] ${
                  formik.errors.leadStatus
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                onClick={() => setShowStatusOptions(!showStatusOptions)}
              >
                <span>{formik.values.leadStatus}</span>
                <span>▼</span>
              </div>
              {showStatusOptions && (
                <div className="mt-2 bg-gray-700 p-4 rounded z-10">
                  {["Hot", "Warm", "Cold", "Customer", "Denied"].map(
                    (status) => (
                      <label
                        key={status}
                        className="flex items-center gap-3 py-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="leadStatus"
                          checked={formik.values.leadStatus === status}
                          onChange={() => {
                            formik.setFieldValue("leadStatus", status);
                            setShowStatusOptions(false);
                          }}
                            onChange={() => {
                              formik.setFieldValue("investmentType", item);
                              setShowInvestmentOptions(false);
                            }}
                          className="w-5 h-5"
                        />
                        <span>{status}</span>
                      </label>
                    )
                  )}
                </div>
              )}
              {formik.touched.leadStatus && formik.errors.leadStatus && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.leadStatus}
                </div>
              )}
            </fieldset> */}

            <fieldset className="border border-white rounded-lg p-6 mb-6">
              <legend className="px-4 text-lg text-center w-auto mx-auto">
                Status
              </legend>
              <CustomRadioSelect
                label="Lead Status"
                name="leadStatus"
                value={formik.values.leadStatus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.leadStatus}
                options={[
                  { value: "Hot", label: "Hot" },
                  { value: "Warm", label: "Warm" },
                  { value: "Cold", label: "Cold" },
                  { value: "Customer", label: "Customer" },
                  { value: "Denied", label: "Denied" },
                ]}
              />
            </fieldset>

            <fieldset className="border border-white rounded-lg p-6">
              <legend className="px-4 text-lg text-center w-auto mx-auto">
                Basic Details
              </legend>
              <div className="grid grid-cols-2 gap-4">
                {isASA && !isEditMode && (
                  <div className="col-span-2">
                    <label className="block mb-2">Assign to Agent</label>
                    <select
                      value={assignedAgentId}
                      onChange={(e) => {
                        setAssignedAgentId(e.target.value);
                        setAgentError("");
                      }}
                      className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                        agentError ? "ring-2 ring-red-500" : ""
                      }`}
                    >
                      <option value="">
                        {asaAgents.length
                          ? "Select one of your agents…"
                          : "No agents assigned to you yet"}
                      </option>
                      {asaAgents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                    {agentError && (
                      <div className="text-red-500 text-sm mt-1">
                        {agentError}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block mb-2">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.firstName && formik.touched.firstName
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.firstName}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.lastName && formik.touched.lastName
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.lastName}
                    </div>
                  )}
                </div>
                {/* <div>
                  <label className="block mb-2">Mobile Number</label>
                  <div className="flex relative">
                    <CountryCodeSelect
                      value={formik.values.countryCode}
                      onChange={(code) =>
                        formik.setFieldValue("countryCode", code)
                      }
                      className="absolute left-5 top-1/2 -translate-y-1/2"
                    />
                    <input
                      type="text"
                      name="mobile"
                      placeholder="Enter..."
                      className={`w-full px-3 py-2 text-[#F4F5F7] rounded-r-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                        formik.errors.mobile && formik.touched.mobile
                          ? "border-red-500 border-2"
                          : "border-gray-300"
                      }`}
                      value={formik.values.mobile}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.mobile && formik.errors.mobile && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.mobile}
                    </div>
                  )}
                </div> */}

                <div>
                  <label className="block mb-2">Mobile Number</label>
                  <div
                    className={`flex items-center w-full rounded-md bg-[#2E4051] border-none focus-within:ring-2 focus-within:ring-[#008564] transition-colors ${
                      formik.errors.mobile && formik.touched.mobile
                        ? "border-red-500 border-2"
                        : "border border-gray-300"
                    }`}
                  >
                    <CountryCodeSelect
                      value={formik.values.countryCode}
                      onChange={(code) =>
                        formik.setFieldValue("countryCode", code)
                      }
                    />
                    <input
                      type="text"
                      name="mobile"
                      placeholder={getPlaceholder()}
                      className="w-full px-3 py-2 text-[#F4F5F7] bg-transparent border-none focus:border-none focus:outline-none focus:ring-0  transition-colors"
                      value={formik.values.mobile}
                      maxLength={getMaxDigits()}
                      readOnly={isEditMode}
                      onChange={(e) => {
                        const onlyDigits = e.target.value.replace(/\D/g, "");
                        formik.setFieldValue("mobile", onlyDigits);

                        // Clear mobile error
                        setMobileError("");

                        // Debounced duplicate check
                        if (mobileCheckTimeout.current) {
                          clearTimeout(mobileCheckTimeout.current);
                        }

                        // Check only when valid phone number length is reached
                        if (
                          isValidPhoneNumber(
                            `${formik.values.countryCode}${onlyDigits}`,
                          )
                        ) {
                          mobileCheckTimeout.current = setTimeout(() => {
                            checkMobile(onlyDigits);
                          }, 800);
                        }
                      }}
                      onBlur={formik.handleBlur}
                    />
                    {checkingMobile && (
                      <div className="pr-3">
                        <svg
                          className="animate-spin h-5 w-5 text-[#008564]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {formik.touched.mobile && formik.errors.mobile && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.mobile}
                    </div>
                  )}
                  {mobileError && (
                    <div className="text-red-500 text-sm mt-1">
                      {mobileError}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block mb-2">Email ID</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter..."
                      className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                        formik.errors.email && formik.touched.email
                          ? "border-red-500 border-2"
                          : "border-gray-300"
                      }`}
                      value={formik.values.email}
                      readOnly={isEditMode}
                      onChange={(e) => {
                        formik.handleChange(e);

                        // Clear email error
                        setEmailError("");

                        // Debounced duplicate check
                        if (emailCheckTimeout.current) {
                          clearTimeout(emailCheckTimeout.current);
                        }

                        emailCheckTimeout.current = setTimeout(() => {
                          checkEmail(e.target.value);
                        }, 800);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    {checkingEmail && (
                      <div className="absolute right-3 top-3">
                        <svg
                          className="animate-spin h-5 w-5 text-[#008564]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.email}
                    </div>
                  )}
                  {emailError && (
                    <div className="text-red-500 text-sm mt-1">
                      {emailError}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Address Line 1</label>
                  <input
                    type="text"
                    name="addressLine1"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.addressLine1 && formik.touched.addressLine1
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.addressLine1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.addressLine1 &&
                    formik.errors.addressLine1 && (
                      <div className="text-red-500 text-sm mt-1">
                        {formik.errors.addressLine1}
                      </div>
                    )}
                </div>
                <div>
                  <label className="block mb-2">Address Line2</label>
                  <input
                    type="text"
                    name="addressLine2"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.addressLine2 && formik.touched.addressLine2
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.addressLine2}
                    onChange={formik.handleChange}
                  />
                </div>
                <div>
                  <label className="block mb-2">Landmark</label>
                  <input
                    type="text"
                    name="landmark"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.landmark && formik.touched.landmark
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.landmark}
                    onChange={formik.handleChange}
                  />
                </div>
                <div>
                  <label className="block mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.city && formik.touched.city
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.city && formik.errors.city && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.city}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.state && formik.touched.state
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.state && formik.errors.state && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.state}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.pincode && formik.touched.pincode
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.pincode && formik.errors.pincode && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.pincode}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">LinkedIn Profile</label>
                  <input
                    type="text"
                    name="linkedIn"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.linkedIn && formik.touched.linkedIn
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.linkedIn}
                    onChange={formik.handleChange}
                  />
                </div>
                <div>
                  <label className="block mb-2">
                    Referred By Mobile Number (Lead Mobile Number)
                  </label>
                  <input
                    type="text"
                    name="referredBy"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.referredBy && formik.touched.referredBy
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.referredBy}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
            </fieldset>
          </div>
        )}

        {activeTab === "additional" && (
          <div className="max-w-4xl mx-auto">
            {/* <fieldset className="border border-gray-600 rounded-lg p-6">
              <legend className="px-4 text-lg">Additional Details</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2">Interested In</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() =>
                      setShowInterestedOptions(!showInterestedOptions)
                    }
                  >
                    <span>
                      {formik.values.interestedIn.length > 0
                        ? formik.values.interestedIn.join(", ")
                        : "Enter..."}
                    </span>
                    <span>▼</span>
                  </div>
                  {showInterestedOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {Array(6)
                        .fill("Villa in Gated Community")
                        .map((item, i) => (
                          <label
                            key={i}
                            className="flex items-center gap-3 py-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="w-5 h-5"
                              checked={formik.values.interestedIn.includes(
                                item
                              )}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...formik.values.interestedIn, item]
                                  : formik.values.interestedIn.filter(
                                      (v) => v !== item
                                    );
                                formik.setFieldValue("interestedIn", newValue);
                              }}
                            />
                            <span>{item}</span>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Investment Type</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() =>
                      setShowInvestmentOptions(!showInvestmentOptions)
                    }
                  >
                    <span>{formik.values.investmentType || "Enter..."}</span>
                    <span>▼</span>
                  </div>
                  {showInvestmentOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {["Primary", "Secondary"].map((item) => (
                        <label
                          key={item}
                          className="flex items-center gap-3 py-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="investmentType"
                            className="w-5 h-5"
                            checked={formik.values.investmentType === item}
                            onChange={() => {
                              formik.setFieldValue("investmentType", item);
                              setShowInvestmentOptions(false);
                            }}
                          />
                          <span>{item}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Budget in INR</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() => setShowBudgetOptions(!showBudgetOptions)}
                  >
                    <span>{formik.values.budgetInr || "Enter..."}</span>
                    <span>▼</span>
                  </div>
                  {showBudgetOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {[
                        "< 1 Cr",
                        "< 1.5 Cr",
                        "< 2 Cr",
                        "< 5 Cr",
                        "< 10 Cr",
                        "< 20 Cr",
                      ].map((item) => (
                        <label
                          key={item}
                          className="flex items-center gap-3 py-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="budgetInr"
                            className="w-5 h-5"
                            checked={formik.values.budgetInr === item}
                            onChange={() => {
                              formik.setFieldValue("budgetInr", item);
                              setShowBudgetOptions(false);
                            }}
                          />
                          <span>{item}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Occupation Type</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() =>
                      setShowOccupationOptions(!showOccupationOptions)
                    }
                  >
                    <span>{formik.values.occupationType || "Enter..."}</span>
                    <span>▼</span>
                  </div>
                  {showOccupationOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {["Salaried", "Employed"].map((item) => (
                        <label
                          key={item}
                          className="flex items-center gap-3 py-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="occupationType"
                            className="w-5 h-5"
                            checked={formik.values.occupationType === item}
                            onChange={() => {
                              formik.setFieldValue("occupationType", item);
                              setShowOccupationOptions(false);
                            }}
                          />
                          <span>{item}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Income Source</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() =>
                      setShowIncomeSourceOptions(!showIncomeSourceOptions)
                    }
                  >
                    <span>{formik.values.incomeSource || "Enter..."}</span>
                    <span>▼</span>
                  </div>
                  {showIncomeSourceOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {["Single Income", "Multi Income"].map((item) => (
                        <label
                          key={item}
                          className="flex items-center gap-3 py-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="incomeSource"
                            className="w-5 h-5"
                            checked={formik.values.incomeSource === item}
                            onChange={() => {
                              formik.setFieldValue("incomeSource", item);
                              setShowIncomeSourceOptions(false);
                            }}
                          />
                          <span>{item}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-2">Family Size</label>
                  <input
                    type="text"
                    name="familySize"
                    placeholder="Enter..."
                    className="w-full bg-gray-700 p-3 rounded"
                    value={formik.values.familySize}
                    onChange={formik.handleChange}
                  />
                </div>
                <div>
                  <label className="block mb-2">Annual Income</label>
                  <select
                    name="annualIncome"
                    className="w-full bg-gray-700 p-3 rounded"
                    value={formik.values.annualIncome}
                    onChange={formik.handleChange}
                  >
                    <option>Enter...</option>
                  </select>
                </div>
              </div>
            </fieldset> */}

            <fieldset className="border border-white rounded-lg p-6">
              <legend className="px-4 text-lg text-center w-auto mx-auto">
                Additional Details
              </legend>
              <div className="grid grid-cols-2 gap-4">
                {/* <div>
                  <label className="block mb-2">Interested In</label>
                  <div
                    className="w-full bg-gray-700 p-3 rounded cursor-pointer flex justify-between items-center"
                    onClick={() =>
                      setShowInterestedOptions(!showInterestedOptions)
                    }
                  >
                    <span>
                      {formik.values.interestedIn.length > 0
                        ? formik.values.interestedIn.join(", ")
                        : "Enter..."}
                    </span>
                    <span>▼</span>
                  </div>
                  {showInterestedOptions && (
                    <div className="mt-2 bg-gray-700 p-4 rounded">
                      {Array(6)
                        .fill("apartment")
                        .map((item, i) => (
                          <label
                            key={i}
                            className="flex items-center gap-3 py-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="w-5 h-5"
                              checked={formik.values.interestedIn.includes(
                                item,
                              )}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...formik.values.interestedIn, item]
                                  : formik.values.interestedIn.filter(
                                      (v) => v !== item,
                                    );
                                formik.setFieldValue("interestedIn", newValue);
                              }}
                            />
                            <span>{item}</span>
                          </label>
                        ))}
                    </div>
                  )}
                </div> */}

                <CustomCheckboxSelect
                  label="Interested In"
                  name="interestedIn"
                  value={formik.values.interestedIn}
                  onChange={(val: any) =>
                    formik.setFieldValue("interestedIn", val)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.errors.interestedIn as string}
                  options={[
                    {
                      value: "layout_gated_community",
                      label: "Layout in Gated Community",
                    },
                    { value: "farmland", label: "Farmland" },
                    {
                      value: "villa_gated_community",
                      label: "Villa in Gated Community",
                    },
                    { value: "apartment", label: "Apartment" },
                  ]}
                />

                <CustomRadioSelect
                  label="Investment Type"
                  name="investmentType"
                  value={formik.values.investmentType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.investmentType}
                  options={[
                    { value: "Primary", label: "Primary" },
                    { value: "Secondary", label: "Secondary" },
                  ]}
                />

                <CustomRadioSelect
                  label="Budget in INR"
                  name="budgetInr"
                  value={formik.values.budgetInr}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.budgetInr}
                  options={[
                    { value: "<1Cr", label: "< 1 Cr" },
                    { value: "<1.5Cr", label: "< 1.5 Cr" },
                    { value: "<2Cr", label: "< 2 Cr" },
                    { value: "<5Cr", label: "< 5 Cr" },
                    { value: "<10Cr", label: "< 10 Cr" },
                    { value: "<20Cr", label: "< 20 Cr" },
                  ]}
                />

                <CustomRadioSelect
                  label="Occupation Type"
                  name="occupationType"
                  value={formik.values.occupationType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.occupationType}
                  options={[
                    { value: "Salaried", label: "Salaried" },
                    { value: "Self Employed", label: "Self Employed" },
                  ]}
                />

                <CustomRadioSelect
                  label="Income Source"
                  name="incomeSource"
                  value={formik.values.incomeSource}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.incomeSource}
                  options={[
                    { value: "Single Income", label: "Single Income" },
                    { value: "Multi Income", label: "Multi Income" },
                  ]}
                />

                <div>
                  <label className="block mb-2">Family Size</label>
                  <input
                    type="text"
                    name="familySize"
                    placeholder="Enter..."
                    className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                      formik.errors.familySize && formik.touched.familySize
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    value={formik.values.familySize}
                    onChange={formik.handleChange}
                  />
                </div>

                <CustomRadioSelect
                  label="Annual Income"
                  name="annualIncome"
                  value={formik.values.annualIncome}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.annualIncome}
                  options={[
                    { value: "<6L", label: "< 6 L" },
                    { value: "<12L", label: "< 12 L" },
                    { value: "<25L", label: "< 25 L" },
                    { value: "<50L", label: "< 50 L" },
                    { value: "<1Cr", label: "< 1 Cr" },
                    { value: ">1Cr", label: "> 1 Cr" },
                  ]}
                />
              </div>
            </fieldset>
          </div>
        )}

        <div className="flex justify-center gap-4 mt-6">
          <button
            type="button"
            onClick={() => {
              if (activeTab === "additional") {
                setActiveTab("basic");
              } else {
                navigate("/leads");
              }
            }}
            // className="bg-gray-700 px-8 py-2 rounded"
            className="bg-[#2E4051] text-xl font-medium text-white px-6 py-2 rounded-lg"
            style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
          >
            {activeTab === "additional" ? "Back" : "Cancel"}
          </button>

          <button
            type="button"
            onClick={handleNext}
            // className="bg-green-600 px-8 py-2 rounded"
            className="bg-[#008564] text-xl font-medium text-white px-6 py-2 rounded-lg"
            style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
          >
            {activeTab === "additional"
              ? formik.isSubmitting
                ? "Saving..."
                : "Save"
              : "Next"}
          </button>
        </div>
      </form>
    </div>
  );
};
